<template>
  <div class="relative isolate py-10 px-6 sm:py-16 lg:px-8 bg-indigo-50/20">
    <div class="mx-auto max-w-2xl sm:text-center lg:max-w-4xl">
      <p class="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
        A pricing model for everyone
      </p>
    </div>
    <p class="mx-auto mt-6 max-w-2xl sm:text-center text-lg leading-8 text-gray-600">
      Whether you're a small business or an agency, we have a solution for you. Pay monthly or
      annually. Cancel anytime.
    </p>
    <div class="max-w-screen-xl mx-auto">
      <div class="mt-16 flex justify-center">
        <RadioGroup v-model="frequency"
                    class="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-200">
          <RadioGroupOption as="template" v-for="option in frequencies" :key="option.value" :value="option"
                            v-slot="{ checked }">
            <div
              :class="[checked ? 'bg-indigo-600 text-white' : 'text-gray-600', 'cursor-pointer rounded-full px-2.5 py-1']">
              <span>{{ option.label }}</span>
              <span v-if="option.value == 'yearly'" class="ml-1.5 opacity-80">Save 20%</span>
            </div>
          </RadioGroupOption>
        </RadioGroup>
      </div>
      <div
        class="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 md:max-w-2xl md:grid-cols-2 lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-4">
        <PricingPlanCard v-for="tier in tiers" :plan="tier" :key="tier.id" @plan-selected="planSelected"/>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RadioGroup, RadioGroupOption } from '@headlessui/vue'
import { ref, watch } from 'vue'
import { Pricing } from '@/utils/constants'
import PricingPlanCard from '@/components/cards/PricingPlanCard.vue'
import type {Plan} from '@/components/cards/PricingPlanCard.vue'
import { useRouter } from 'vue-router'
import { windowLoaded } from '@/utils/functions'

const router = useRouter()

const frequencies = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' }
]

const frequency = ref(frequencies[1])

const tiers = ref<Plan[]>([
  {
    id: 'starter',
    name: 'Starter',
    description: 'For businesses looking to have minimal social presence.',
    mostPopular: false,
    customPricing: false,
    price: { monthly: Pricing.StarterMonthly, yearly: Pricing.StarterAnnually },
    selectedFrequency: frequency.value.value,
    buttonLabel: 'Start 7 day free trial',
    featuresHeadline: "Features:",
    features: [
      'Up to 6 collaborations per month',
      'Access to detailed creator statistics',
      'Single location access',
      'Multi-brand access',
      'Team management',
      'Email support'
    ],
  },


  {
    id: 'basic',
    name: 'Basic',
    description: 'For businesses looking to ramp-up their social presence.',
    mostPopular: true,
    customPricing: false,
    price: { monthly: Pricing.BasicMonthly, yearly: Pricing.BasicAnnually },
    selectedFrequency: frequency.value.value,
    buttonLabel: 'Start 7 day free trial',
    featuresHeadline: "Everything in Starter, plus:",
    features: [
      'Up to 12 collaborations per month',
      'Up to 3 locations access',
      'Priority email support'
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'For businesses betting on social media to drive growth.',
    mostPopular: false,
    customPricing: false,
    price: { monthly: Pricing.ProMonthly, yearly: Pricing.ProAnnually },
    selectedFrequency: frequency.value.value,
    buttonLabel: 'Book a demo',
    featuresHeadline: "Everything in Basic, plus:",
    features: [
      'Up to 24 collaborations per month',
      'Up to 6 locations access',
      'Phone support',
      'Feature request priority',
    ],
  },
  {
    id: 'custom',
    name: 'Custom',
    description: 'For agencies or larger businesses looking to add more than 3 brands',
    mostPopular: false,
    customPricing: true,
    price: { monthly: 0, yearly: 0 },
    selectedFrequency: frequency.value.value,
    buttonLabel: 'Book a demo',
    featuresHeadline: "Everything in Pro, plus:",
    features: [
      'Brands with more than 6 locations',
      'Discount options for additional brands',
    ],
  }
])

watch(frequency, (newValue) => {
  tiers.value.forEach(item => {
    item.selectedFrequency = newValue.value
  })
})

function planSelected(id: string) {
  if (id == 'basic') {
    router.push({path: '/business/signup'})
  } else {
    showCalendly()
  }
}

async function showCalendly() {
  window.location = "https://calendly.com/bulba/30-min-call"
}
</script>
